import React, { useMemo, useEffect, useContext } from 'react';
import color from 'color';
import { jsx } from 'react/jsx-runtime';

let Curvature;
(function (Curvature) {
  Curvature[Curvature["OFF"] = 0] = "OFF";
  Curvature[Curvature["WEAK"] = 1] = "WEAK";
  Curvature[Curvature["MEDIUM"] = 3] = "MEDIUM";
  Curvature[Curvature["STRONG"] = 2] = "STRONG";
})(Curvature || (Curvature = {}));
let TypographySizing;
(function (TypographySizing) {
  TypographySizing[TypographySizing["SMALL"] = 0] = "SMALL";
  TypographySizing[TypographySizing["MEDIUM"] = 1] = "MEDIUM";
  TypographySizing[TypographySizing["LARGE"] = 2] = "LARGE";
})(TypographySizing || (TypographySizing = {}));
let Spacing;
(function (Spacing) {
  Spacing[Spacing["SMALL"] = 0] = "SMALL";
  Spacing[Spacing["MEDIUM"] = 1] = "MEDIUM";
  Spacing[Spacing["LARGE"] = 2] = "LARGE";
})(Spacing || (Spacing = {}));
let GridUnit;
(function (GridUnit) {
  GridUnit[GridUnit["SMALL"] = 6] = "SMALL";
  GridUnit[GridUnit["MEDIUM"] = 8] = "MEDIUM";
  GridUnit[GridUnit["LARGE"] = 10] = "LARGE";
})(GridUnit || (GridUnit = {}));
let Colorway;
(function (Colorway) {
  Colorway["LIGHT"] = "LIGHT";
  Colorway["NEUTRAL"] = "NEUTRAL";
  Colorway["DARK"] = "DARK";
  Colorway["PRIMARY"] = "PRIMARY";
  Colorway["PRIMARY_GRADIENT"] = "PRIMARY_GRADIENT";
  Colorway["HIGHLIGHT"] = "HIGHLIGHT";
})(Colorway || (Colorway = {}));

const createTheme = () => {
  return {
    palette: {
      headerBackground: '#0269A0',
      headerForeground: '#FFFFFF',
      headerHighlight: '',
      primary: '#0091d3',
      primaryContrast: '#ffffff',
      primaryGradient: '#0091D3',
      secondary: '#23699f',
      tertiary: '#0091d3',
      highlight: '#e0234b',
      highlightContrast: '#ffffff',
      cta: '#83b702',
      ctaContrast: '#ffffff',
      contrast: '#fff',
      illustrations: {
        primary: '#0093d1',
        primaryContrast: '#ffffff',
        secondary: '#8dc63f',
        secondaryContrast: '#ffffff',
        tertiary: '#e1144d',
        tertiaryContrast: '#fff'
      },
      grey01: '#1a1a1a',
      grey02: '#282828',
      grey03: '#333',
      grey04: '#484848',
      grey05: '#666',
      grey06: '#848484',
      grey07: '#909090',
      grey08: '#999',
      grey09: '#ccc',
      grey10: '#eee',
      grey11: '#f2f2f2',
      grey12: '#f7f7f7',
      red01: '#cc0000',
      red02: '#f4c7c7',
      yellow01: '#EDD000',
      yellow02: '#FAF1B2',
      green01: '#7FA800',
      green02: '#D8E5B2'
    },
    typography: {
      content: 'sans-serif',
      display: 'serif',
      contentURL: 'https://fonts.googleapis.com/css?family=Open+Sans',
      displayURL: 'https://fonts.googleapis.com/css?family=Raleway',
      sizing: 1
    },
    brandLogos: {
      logoLight: 'https://static-artifact.heg-cp.com/assets/Logo-123Reg-Light.svg',
      logoDark: 'https://static-artifact.heg-cp.com/assets/Logo-123Reg-Dark.svg',
      logoContrast: 'https://static-artifact.heg-cp.com/assets/Logo-123Reg-Dark.svg'
    },
    curvature: 0,
    spacing: 1,
    iconSet: 'informal-filled'
  };
};
var createTheme$1 = createTheme;

const guMap = {
  [Spacing.SMALL]: GridUnit.SMALL,
  [Spacing.MEDIUM]: GridUnit.MEDIUM,
  [Spacing.LARGE]: GridUnit.LARGE
};

/**
 * Grid units to pixels(number). Default spacing is MEDIUM.
 *
 * @param gridUnits the amount of units we want. eg: 2
 * @param spacing default Spacing.MEDIUM
 * @returns the grid units converted to number
 */
const gu2px = (gridUnits, spacing) => {
  const gu = guMap[spacing != null ? spacing : Spacing.MEDIUM];
  return Math.floor(gridUnits * gu);
};

/**
 * Receives a hex color string and transform into
 * a rgb 'red, green, blue' comma separated.
 *
 * @param hex hex color string
 * @returns the received hex color parsed to rgb. eg: '244, 233, 222'.
 */
const hex2RgbString = hex => {
  if (!hex) {
    return '';
  }
  try {
    const [red, green, blue] = color(hex).rgb().array();
    return `${red}, ${green}, ${blue}`;
  } catch (e) {
    return '';
  }
};

/**
 * Transforms pixels to rem.
 *
 * @param px pixel val. eg: 12
 *
 * @returns (12/16) = 0.75rem
 */
const pxToRem = px => `${px / 16}rem`;

/**
 * Calculates the line height based in font size.
 *
 * @param fontSize
 * @returns 1.5 | 1.4 | 1.3
 */
const calculateLineHeight = fontSize => {
  if (fontSize <= 16) {
    return 1.5;
  }
  if (fontSize <= 32) {
    return 1.4;
  }
  return 1.3;
};

/**
 * Mixes two CSS colors with a determined weight resulting a combination of them.
 *
 * Note: it uses the npm package 'color' for color mixing.
 *
 * @param colorA    first color to mix
 * @param colorB    second color to mix with the given first color
 * @param mixWeight the weight considered on mix
 *
 * @returns the colors mixed, if one of them is invalid it returns the other, if both are invalid it returns an empty string ''
 */
const mix = (colorA, colorB, mixWeight) => {
  if (!colorA && !colorB) {
    return '';
  }
  const aIsValid = isColor(colorA);
  const bIsValid = isColor(colorB);
  if (!aIsValid || !bIsValid) {
    return '';
  }

  /**
   * At this point A and B are valid lets mix them.
   */
  return color(colorA).mix(color(colorB), mixWeight).string();
};

/**
 * Checks if a color is processable by the browser.
 *
 * @param strColor
 * @returns true if the color is a valid CSS color
 */
const isColor = strColor => {
  try {
    color(strColor);
    return true;
  } catch (e) {
    return false;
  }
};

/**
 * build CSS variables from theme object, prepend 'theme'
 * --theme-palette-primary: #color
 * --theme-palette-illustrations-primary: #color
 */
const buildCssVarsString = cssTheme => {
  return Object.keys(cssTheme).reduce((prev, cur) => {
    return `${prev}--theme-${cur}: ${cssTheme[cur]};`;
  }, '');
};

const buildColorway = palette => {
  const colorway = {
    // bookends
    'bookends-background': '#fff',
    'bookends-text-rgb': `${hex2RgbString(palette.grey02)}`,
    'bookends-secondary': palette.primary,
    'bookends-secondaryContrast': palette.secondary,
    'bookends-tertiary': palette.grey12,
    'bookends-highlight': palette.highlight,
    // badge
    'badge-tag': palette.grey02,
    'badge-primary-color': palette.primary,
    'badge-primary-text': palette.primaryContrast,
    'badge-highlight-color': palette.highlight,
    'badge-highlight-text': palette.highlightContrast,
    'badge-warning-color': palette.yellow01,
    'badge-warning-text': palette.grey02,
    'badge-alert-color': palette.highlight,
    'badge-alert-text': '#fff',
    'badge-neutral-color': palette.grey02,
    'badge-neutral-text': '#fff',
    'badge-muted-color': palette.grey11,
    'badge-muted-text': palette.grey02,
    // loader
    'loader-color': palette.grey06,
    // panel
    'panel-text-color': 'inherit',
    'panel-alert-color': palette.red01,
    'panel-warning-color': palette.yellow01,
    'panel-neutral-color': palette.grey02,
    'panel-primary-color': palette.primary,
    'panel-highlight-color': palette.highlight,
    // text button
    'textButton-color': palette.secondary,
    // buttons
    'button-primary-color': palette.cta,
    'button-primary-text': palette.ctaContrast,
    // we need this value to overwrite colorway defaults for action buttons inside inputs
    'button-primary-hover-color-light-colorway': mix(palette.cta, palette.ctaContrast, 0.1),
    'button-primary-hover-color': mix(palette.cta, palette.ctaContrast, 0.1),
    'button-primary-hover-colorContrast': mix(palette.primaryContrast, palette.primary, 0.1),
    'button-primary-hover-border': `rgba(${hex2RgbString(palette.ctaContrast)}, 0.2)`,
    'button-secondary-text': `rgba(${hex2RgbString(palette.grey01)}, 0.85)`,
    'button-secondary-border': `rgba(${hex2RgbString(palette.grey01)}, 0.75)`,
    'button-secondary-hover-text': palette.grey01,
    'button-secondary-hover-border': palette.grey01,
    'button-tertiary-text': `rgba(${hex2RgbString(palette.grey01)}, 0.85)`,
    'button-tertiary-border': `rgba(${hex2RgbString(palette.grey09)}, 0.75)`,
    'button-tertiary-hover-text': palette.grey01,
    'button-tertiary-hover-border': palette.grey01,
    'button-highlight-color': palette.highlight,
    'button-highlight-text': palette.highlightContrast,
    'button-highlight-hover-color': mix(palette.highlight, palette.highlightContrast, 0.05),
    'button-highlight-hover-border': `rgba(${hex2RgbString(palette.highlightContrast)}, 0.2)`,
    'button-detrimental-color': palette.red01,
    'button-detrimental-text': '#fff',
    'button-detrimental-hover-color': mix(palette.red01, '#fff', 0.1),
    // text
    'error-text': palette.red01,
    // shimmer
    'shimmer-color-base': palette.grey11,
    'shimmer-color': `rgba(${hex2RgbString(palette.grey09)}, 0.5)`
  };
  return colorway;
};
const lightColorwayClassName = 'UX-theme-utils-light-colorway';
const buildLightColorway = palette => {
  return buildCssVarsString(buildColorway(palette));
};

const Context = /*#__PURE__*/React.createContext(undefined);
const makeGetStyleTag = () => {
  let uxThemeTag = null;
  return () => {
    if (uxThemeTag === null) {
      uxThemeTag = document.querySelector('style[data-ux-theme]');
      if (uxThemeTag === null) {
        // Still `null` so let's create the style element:
        const htmlHead = document.head;
        uxThemeTag = document.createElement('style');
        uxThemeTag.type = 'text/css';
        uxThemeTag.setAttribute('data-ux-theme', '');
        htmlHead === null || htmlHead === void 0 ? void 0 : htmlHead.appendChild(uxThemeTag);
      }
    }
    const styleSheet = uxThemeTag.sheet;
    return styleSheet;
  };
};
const getStyleTag = makeGetStyleTag();
const ThemeProvider = _ref => {
  let {
    children,
    theme
  } = _ref;
  const state = useMemo(() => ({
    theme
  }), [theme]);
  useEffect(() => {
    const styleSheet = getStyleTag();
    // This rule has to be generated runtime, since it depends on mix function and rgb values
    // Let's generate the class here, rather than having it bleed into other packages
    // We export the class name for colorways to reuse
    if (styleSheet != null) {
      if (styleSheet.cssRules.length > 0) {
        // This is mostly for storybook, but we need to clean up the styles when switching brand
        styleSheet.deleteRule(0);
      }
      styleSheet.insertRule(`.${lightColorwayClassName} {
        ${buildLightColorway(theme.palette)}
      }`, 0);
    }
  }, [theme]);
  return /*#__PURE__*/jsx(Context.Provider, {
    value: state,
    children: children
  });
};
ThemeProvider.displayName = 'ThemeProvider';
const useTheme = () => {
  const theme = useContext(Context);
  if (theme == null) {
    throw new Error('The theme was undefined, did you forget to add the ThemeProvider?');
  }
  return theme;
};

const small = {
  xxs: {
    fontSize: 14
  },
  xs: {
    fontSize: 16
  },
  s: {
    fontSize: 18
  },
  m: {
    fontSize: 21
  },
  l: {
    fontSize: 24
  },
  xl: {
    fontSize: 28
  },
  xxl: {
    fontSize: 32
  },
  display: {
    fontSize: 48
  }
};
const medium = {
  xxs: {
    fontSize: 16
  },
  xs: {
    fontSize: 18
  },
  s: {
    fontSize: 21
  },
  m: {
    fontSize: 24
  },
  l: {
    fontSize: 28
  },
  xl: {
    fontSize: 32
  },
  xxl: {
    fontSize: 36
  },
  display: {
    fontSize: 55
  }
};
const large = {
  xxs: {
    fontSize: 18
  },
  xs: {
    fontSize: 21
  },
  s: {
    fontSize: 24
  },
  m: {
    fontSize: 28
  },
  l: {
    fontSize: 32
  },
  xl: {
    fontSize: 36
  },
  xxl: {
    fontSize: 42
  },
  display: {
    fontSize: 63
  }
};
const getSizes = size => {
  switch (size) {
    case TypographySizing.SMALL:
      return small;
    case TypographySizing.LARGE:
      return large;
    default:
      return medium;
  }
};
const buildTypeSizes = size => {
  const typographySizes = {
    'typography-sizing-smallprint': pxToRem(12),
    'typography-sizing-footnote': pxToRem(14),
    'typography-sizing-content': pxToRem(16),
    'typography-line-height-smallprint': calculateLineHeight(12),
    'typography-line-height-footnote': calculateLineHeight(14),
    'typography-line-height-content': calculateLineHeight(16)
  };
  for (const [key, {
    fontSize
  }] of Object.entries(getSizes(size))) {
    typographySizes[`typography-sizing-${key}`] = pxToRem(fontSize);
    typographySizes[`typography-line-height-${key}`] = calculateLineHeight(fontSize);
  }
  return typographySizes;
};

const flattenObj = function (obj, parent) {
  let res = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : {};
  for (const key in obj) {
    if (Object.prototype.hasOwnProperty.call(obj, key) === true) {
      const propName = parent == null ? key : `${parent}-${key}`;
      if (typeof obj[key] === 'object') {
        flattenObj(obj[key], propName, res);
      } else {
        res[propName] = obj[key];
      }
    }
  }
  return res;
};
const buildCssTheme = theme => {
  if (typeof theme !== 'object' || theme === null) {
    // eslint-disable-next-line no-console
    console.error('Invalid theme provided for CSS variables');
    return {};
  }

  // flatten in prep for mapping to css vars
  const flatTheme = flattenObj(theme);

  // add additional css variables for GU spacing
  // since we cannot do dynamic runtime stuff in css
  const guSizes = flattenObj({
    gu: {
      1: `${gu2px(1, theme.spacing)}px`,
      2: `${gu2px(2, theme.spacing)}px`,
      3: `${gu2px(3, theme.spacing)}px`,
      4: `${gu2px(4, theme.spacing)}px`,
      5: `${gu2px(5, theme.spacing)}px`,
      6: `${gu2px(6, theme.spacing)}px`,
      8: `${gu2px(8, theme.spacing)}px`,
      10: `${gu2px(10, theme.spacing)}px`,
      12: `${gu2px(12, theme.spacing)}px`,
      16: `${gu2px(16, theme.spacing)}px`
    }
  });
  const curvatureMap = (initialValue, weak, medium, strong) => {
    const calculateCurvature = value => {
      return `${gu2px(value, theme.spacing)}px`;
    };
    switch (theme.curvature) {
      case Curvature.STRONG:
        return calculateCurvature(strong);
      case Curvature.MEDIUM:
        return calculateCurvature(medium);
      case Curvature.WEAK:
        return calculateCurvature(weak);
      default:
        return calculateCurvature(initialValue);
    }
  };

  // create curvature variables
  // https://app.abstract.com/projects/0fcbbd30-5ae2-11e9-9532-c5e54bca9f88/branches/master/commits/ed08185bc22bcb9276a40e50b7cf42db5c64827f/files/120347AF-09C1-4D6A-927A-3469629C7482/layers/057E0525-6A54-41C5-9161-56914FFF2F15
  const radius = flattenObj({
    radius: {
      panel: curvatureMap(0, 1, 2, 3),
      switch: {
        outer: curvatureMap(0, 3, 3, 3),
        inner: curvatureMap(0, 2, 2, 2)
      },
      button: {
        small: curvatureMap(0, 0.5, 1, 99),
        medium: curvatureMap(0, 0.5, 2, 99),
        large: curvatureMap(0, 1, 2, 99)
      },
      input: {
        small: curvatureMap(0, 0.5, 1, 1.5),
        medium: curvatureMap(0, 0.5, 1, 1.5),
        large: curvatureMap(0, 1, 2, 99)
      },
      tag: curvatureMap(0, 1, 1, 99),
      badge: curvatureMap(0, 0.5, 1, 1.5),
      modal: curvatureMap(0, 1, 1, 2.25),
      checkbox: curvatureMap(0, 0.5, 0.5, 0.5),
      radio: curvatureMap(0.5, 0.88, 0.88, 0.88),
      search: {
        box: curvatureMap(0, 0.5, 1, 99)
      },
      currency: curvatureMap(0, 0.5, 1.5, 3),
      shimmer: curvatureMap(0, 0.5, 1, 1)
    }
  });

  // build the RGB colors for palette, required for opacity functions
  // palette-primary-rgb: '255, 255, 255'
  const rgbPalette = Object.keys(flatTheme).filter(k => k.includes('palette')).reduce((prev, cur) => ({
    ...prev,
    [`${cur}-rgb`]: hex2RgbString(flatTheme[cur])
  }), {});
  const typographySizing = buildTypeSizes(theme.typography.sizing);
  const font = {
    'font-content': theme.typography.content,
    'font-display': theme.typography.display
  };
  const blockPadding = {
    'block-padding-sm': `${gu2px(3, theme.spacing)}px`,
    'block-padding-l': `${gu2px(4, theme.spacing)}px`
  };
  const defaultColorway = buildColorway(theme.palette);
  const cssTheme = Object.assign({}, flatTheme, guSizes, rgbPalette, radius, typographySizing, font, defaultColorway, blockPadding);
  return cssTheme;
};

// take the theme object and return a set of
// css variables for the theme
const buildCssVars = theme => {
  const cssTheme = buildCssTheme(theme);
  const cssVarsString = buildCssVarsString(cssTheme);
  return `:root { ${cssVarsString} }`;
};

// A reduced object version of the theme that returns common css vars
// for simplicity and to save repetition and keep it as familiar as possible
// e.g.
// import { cssTheme } from '@ux/theme-utils';
// {
//   backgroundColor: cssTheme.palette.primary,
// }

const breakpoints = {
  xs: 0,
  sm: 750,
  md: 1020,
  lg: 1300,
  xl: 1800
};
const varPrefix = property => `var(--theme-${property})`;
const cssTheme = {
  curvature: varPrefix('curvature'),
  spacing: varPrefix('spacing'),
  gu1: varPrefix('gu-1'),
  gu2: varPrefix('gu-2'),
  gu3: varPrefix('gu-3'),
  gu4: varPrefix('gu-4'),
  gu5: varPrefix('gu-5'),
  gu6: varPrefix('gu-6'),
  gu8: varPrefix('gu-8'),
  gu10: varPrefix('gu-10'),
  gu12: varPrefix('gu-12'),
  gu16: varPrefix('gu-16'),
  radius: {
    panel: varPrefix('radius-panel'),
    button: {
      small: varPrefix('radius-button-small'),
      medium: varPrefix('radius-button-medium'),
      large: varPrefix('radius-button-large')
    },
    input: {
      small: varPrefix('radius-input-small'),
      medium: varPrefix('radius-input-medium'),
      large: varPrefix('radius-input-large')
    },
    tag: varPrefix('radius-tag'),
    badge: varPrefix('radius-badge'),
    modal: varPrefix('radius-modal'),
    switch: {
      inner: varPrefix('radius-switch-inner'),
      outer: varPrefix('radius-switch-outer')
    },
    checkbox: varPrefix('radius-checkbox'),
    radio: varPrefix('radius-radio'),
    search: {
      box: varPrefix('radius-search-box')
    },
    currency: varPrefix('radius-currency'),
    shimmer: varPrefix('radius-shimmer')
  },
  palette: {
    headerBackground: varPrefix('palette-headerBackground'),
    headerForeground: varPrefix('palette-headerForeground'),
    headerHighlight: varPrefix('palette-headerHighlight'),
    primary: varPrefix('palette-primary'),
    primaryContrast: varPrefix('palette-primaryContrast'),
    primaryGradient: varPrefix('palette-primaryGradient'),
    secondary: varPrefix('palette-secondary'),
    tertiary: varPrefix('palette-tertiary'),
    highlight: varPrefix('palette-highlight'),
    highlightContrast: varPrefix('palette-highlightContrast'),
    accent: varPrefix('palette-accent'),
    contrast: varPrefix('palette-contrast'),
    contrastAccent: varPrefix('palette-contrastAccent'),
    cta: varPrefix('palette-cta'),
    ctaContrast: varPrefix('palette-ctaContrast'),
    errorText: varPrefix('error-text'),
    illustrations: {
      primary: varPrefix('palette-illustrations-primary'),
      primaryContrast: varPrefix('palette-illustrations-primaryContrast'),
      secondary: varPrefix('palette-illustrations-secondary'),
      secondaryContrast: varPrefix('palette-illustrations-secondaryContrast'),
      tertiary: varPrefix('palette-illustrations-tertiary'),
      tertiaryContrast: varPrefix('palette-illustrations-tertiaryContrast')
    },
    grey01: varPrefix('palette-grey01'),
    grey02: varPrefix('palette-grey02'),
    grey03: varPrefix('palette-grey03'),
    grey04: varPrefix('palette-grey04'),
    grey05: varPrefix('palette-grey05'),
    grey06: varPrefix('palette-grey06'),
    grey07: varPrefix('palette-grey07'),
    grey08: varPrefix('palette-grey08'),
    grey09: varPrefix('palette-grey09'),
    grey10: varPrefix('palette-grey10'),
    grey11: varPrefix('palette-grey11'),
    grey12: varPrefix('palette-grey12'),
    red01: varPrefix('palette-red01'),
    red02: varPrefix('palette-red02'),
    yellow01: varPrefix('palette-yellow01'),
    yellow02: varPrefix('palette-yellow02'),
    green01: varPrefix('palette-green01'),
    green02: varPrefix('palette-green02'),
    // RGB versions
    rgb: {
      headerBackground: varPrefix('palette-headerBackground-rgb'),
      headerForeground: varPrefix('palette-headerForeground-rgb'),
      headerHighlight: varPrefix('palette-headerHighlight-rgb'),
      primary: varPrefix('palette-primary-rgb'),
      primaryContrast: varPrefix('palette-primaryContrast-rgb'),
      primaryGradient: varPrefix('palette-primaryGradient-rgb'),
      secondary: varPrefix('palette-secondary-rgb'),
      tertiary: varPrefix('palette-tertiary-rgb'),
      highlight: varPrefix('palette-highlight-rgb'),
      highlightContrast: varPrefix('palette-highlightContrast-rgb'),
      accent: varPrefix('palette-accent-rgb'),
      contrast: varPrefix('palette-contrast-rgb'),
      contrastAccent: varPrefix('palette-contrastAccent-rgb'),
      cta: varPrefix('palette-cta-rgb'),
      ctaContrast: varPrefix('palette-ctaContrast-rgb'),
      illustrations: {
        primary: varPrefix('palette-illustrations-primary-rgb'),
        primaryContrast: varPrefix('palette-illustrations-primaryContrast-rgb'),
        secondary: varPrefix('palette-illustrations-secondary-rgb'),
        secondaryContrast: varPrefix('palette-illustrations-secondaryContrast-rgb'),
        tertiary: varPrefix('palette-illustrations-tertiary-rgb'),
        tertiaryContrast: varPrefix('palette-illustrations-tertiaryContrast-rgb')
      },
      grey01: varPrefix('palette-grey01-rgb'),
      grey02: varPrefix('palette-grey02-rgb'),
      grey03: varPrefix('palette-grey03-rgb'),
      grey04: varPrefix('palette-grey04-rgb'),
      grey05: varPrefix('palette-grey05-rgb'),
      grey06: varPrefix('palette-grey06-rgb'),
      grey07: varPrefix('palette-grey07-rgb'),
      grey08: varPrefix('palette-grey08-rgb'),
      grey09: varPrefix('palette-grey09-rgb'),
      grey10: varPrefix('palette-grey10-rgb'),
      grey11: varPrefix('palette-grey11-rgb'),
      grey12: varPrefix('palette-grey12-rgb'),
      red01: varPrefix('palette-red01-rgb'),
      red02: varPrefix('palette-red02-rgb'),
      yellow01: varPrefix('palette-yellow01-rgb'),
      yellow02: varPrefix('palette-yellow02-rgb'),
      green01: varPrefix('palette-green01-rgb'),
      green02: varPrefix('palette-green02-rgb')
    }
  },
  font: {
    display: varPrefix('font-display'),
    content: varPrefix('font-content')
  },
  typography: {
    /**
     * @deprecated The property should not be used, and will be removed in the next major release. Use font.display instead
     */
    display: varPrefix('typography-display'),
    /**
     * @deprecated The property should not be used, and will be removed in the next major release. Use font.content instead
     */
    content: varPrefix('typography-content'),
    sizing: varPrefix('typography-sizing'),
    lineHeight: {
      xxs: varPrefix('typography-line-height-xxs'),
      xs: varPrefix('typography-line-height-xs'),
      s: varPrefix('typography-line-height-s'),
      m: varPrefix('typography-line-height-m'),
      l: varPrefix('typography-line-height-l'),
      xl: varPrefix('typography-line-height-xl'),
      xxl: varPrefix('typography-line-height-xxl'),
      display: varPrefix('typography-line-height-display'),
      smallprint: varPrefix('typography-line-height-smallprint'),
      footnote: varPrefix('typography-line-height-footnote'),
      content: varPrefix('typography-line-height-content')
    },
    sizes: {
      xxs: varPrefix('typography-sizing-xxs'),
      xs: varPrefix('typography-sizing-xs'),
      s: varPrefix('typography-sizing-s'),
      m: varPrefix('typography-sizing-m'),
      l: varPrefix('typography-sizing-l'),
      xl: varPrefix('typography-sizing-xl'),
      xxl: varPrefix('typography-sizing-xxl'),
      display: varPrefix('typography-sizing-display'),
      smallprint: varPrefix('typography-sizing-smallprint'),
      footnote: varPrefix('typography-sizing-footnote'),
      content: varPrefix('typography-sizing-content')
    }
  },
  breakpoints: {
    sm: `@media (min-width: ${breakpoints.sm}px)`,
    md: `@media (min-width: ${breakpoints.md}px)`,
    lg: `@media (min-width: ${breakpoints.lg}px)`,
    xl: `@media (min-width: ${breakpoints.xl}px)`
  },
  bookends: {
    background: varPrefix('bookends-background'),
    textRgb: varPrefix('bookends-text-rgb'),
    secondary: varPrefix('bookends-secondary'),
    secondaryContrast: varPrefix('bookends-secondaryContrast'),
    tertiary: varPrefix('bookends-tertiary'),
    highlight: varPrefix('bookends-highlight')
  },
  badge: {
    tag: varPrefix('badge-tag'),
    primary: {
      color: varPrefix('badge-primary-color'),
      text: varPrefix('badge-primary-text')
    },
    highlight: {
      color: varPrefix('badge-highlight-color'),
      text: varPrefix('badge-highlight-text')
    },
    warning: {
      color: varPrefix('badge-warning-color'),
      text: varPrefix('badge-warning-text')
    },
    alert: {
      color: varPrefix('badge-alert-color'),
      text: varPrefix('badge-alert-text')
    },
    neutral: {
      color: varPrefix('badge-neutral-color'),
      text: varPrefix('badge-neutral-text')
    },
    muted: {
      color: varPrefix('badge-muted-color'),
      text: varPrefix('badge-muted-text')
    }
  },
  loader: varPrefix('loader-color'),
  panel: {
    text: varPrefix('panel-text-color'),
    alert: varPrefix('panel-alert-color'),
    warning: varPrefix('panel-warning-color'),
    neutral: varPrefix('panel-neutral-color'),
    primary: varPrefix('panel-primary-color'),
    highlight: varPrefix('panel-highlight-color')
  },
  textButton: varPrefix('textButton-color'),
  button: {
    primary: {
      color: varPrefix('button-primary-color'),
      text: varPrefix('button-primary-text'),
      hover: {
        hoverColorLightColorway: varPrefix('button-primary-hover-color-light-colorway'),
        color: varPrefix('button-primary-hover-color'),
        colorContrast: varPrefix('button-primary-hover-colorContrast'),
        border: varPrefix('button-primary-hover-border')
      }
    },
    secondary: {
      text: varPrefix('button-secondary-text'),
      border: varPrefix('button-secondary-border'),
      hover: {
        text: varPrefix('button-secondary-hover-text'),
        border: varPrefix('button-secondary-hover-border')
      }
    },
    tertiary: {
      text: varPrefix('button-tertiary-text'),
      border: varPrefix('button-tertiary-border'),
      hover: {
        text: varPrefix('button-tertiary-hover-text'),
        border: varPrefix('button-tertiary-hover-border')
      }
    },
    highlight: {
      color: varPrefix('button-highlight-color'),
      text: varPrefix('button-highlight-text'),
      hover: {
        color: varPrefix('button-highlight-hover-color'),
        border: varPrefix('button-highlight-hover-border')
      }
    },
    detrimental: {
      color: varPrefix('button-detrimental-color'),
      text: varPrefix('button-detrimental-text'),
      hover: {
        color: varPrefix('button-detrimental-hover-color')
      }
    }
  },
  zIndex: {
    default: '1',
    sticky: '100',
    modal: '800',
    popover: '700',
    onTop: '900'
  },
  shimmer: {
    base: varPrefix('shimmer-color-base'),
    color: varPrefix('shimmer-color')
  },
  block: {
    paddingSM: varPrefix('block-padding-sm'),
    paddingL: varPrefix('block-padding-l')
  }
};

export { Colorway, Curvature, GridUnit, Spacing, ThemeProvider, TypographySizing, breakpoints, buildCssVars, buildCssVarsString, buildLightColorway, calculateLineHeight, createTheme$1 as createTestTheme, cssTheme, gu2px, hex2RgbString, isColor, lightColorwayClassName, mix, pxToRem, useTheme };
